import type { BudgetLineRow, VoucherAllocationRow } from "@/hooks/use-project-detail";
import { groupAllocationsByBudgetLine } from "@/lib/voucher-allocations";

/**
 * VIB-1295: "Påløpt"-kolonnen på Tilbud-fanen — faktisk ekstern kostnad per
 * budsjettlinje, til sammenligning med `estimated_cost`.
 *
 * v1-avgrensning (Erik, bølge-4-spec 15.07): kun `approved`-bilag telles —
 * samme regel som dagens "x bilag · y kr"-badge på Timetildeling
 * (`groupAllocationsByBudgetLine`, `src/lib/voucher-allocations.ts`).
 * `forwarded_to_dl` telles IKKE her selv om prosjektrapporten (VIB-1068,
 * bølge 1) bruker `approved`+`forwarded_to_dl` for "påløpt ekstern kost" på
 * prosjektnivå — de to visningene har bevisst forskjellig avgrensning i v1
 * (se åpent spørsmål i VIB-1295). Ingen ny KPI, ingen BF-writer — ren
 * presentasjon bygget oppå den eksisterende, testede grupperingsregelen.
 *
 * Interne linjer uten bilag skal vises som "—" i UI (ikke 0 kr) — denne
 * funksjonen returnerer derfor kun oppføringer for linjer som FAKTISK har
 * minst én approved-allokering. Kallstedet avgjør "—"-visningen ved fravær
 * av en Map-oppføring.
 */
export function computeBudgetLineActualCost(
  budgetLines: Pick<BudgetLineRow, "id">[],
  allocations: VoucherAllocationRow[],
): Map<string, number> {
  const grouped = groupAllocationsByBudgetLine(allocations);
  const lineIds = new Set(budgetLines.map((l) => l.id));
  const result = new Map<string, number>();
  for (const [budgetLineId, { amount }] of grouped) {
    if (!lineIds.has(budgetLineId)) continue;
    result.set(budgetLineId, amount);
  }
  return result;
}

/**
 * Avviksgrad for VarianceBadge-visning: (påløpt − estimert) / estimert × 100.
 * Positivt = påløpt kostnad overstiger estimatet (dårlig). `null` når det
 * ikke finnes noe meningsfullt estimat å sammenligne mot (unngår
 * divide-by-zero-støy for linjer uten `estimated_cost`).
 */
export function calcActualCostVariancePct(
  actualCost: number | undefined,
  estimatedCost: number | null | undefined,
): number | null {
  if (actualCost == null) return null;
  const est = estimatedCost ?? 0;
  if (est <= 0) return null;
  return ((actualCost - est) / est) * 100;
}
